import { useFlags } from "launchdarkly-react-client-sdk";
import { useEnvironmentType } from "../contexts/useEnvType";
import { useMyFeatureFlagsQuery } from "../types";

export type NutritionFlags = {
  isRecipeAndMenuItemRedesignEnabled: boolean;
  isTeamworksUserManagementEnabled: boolean;
  isAppSwitcherEnabled: boolean;
  showAxleUpsertTab: boolean;
  showDeleteAthleteButton: boolean;
  isUnityMessagingEnabled: boolean;
  isAmsDashboardEnabled: boolean;
  isEnergyExpenditureEnabled: boolean;
  isBulkDeleteMealPlansEnabled: boolean;
  isMenuBuilderMultiWeekCopyingEnabled: boolean;
  isAxleEventsEnabled: boolean;
  isUmaNotificationsEnabled: boolean;
  isBulkOrderingEnabled: boolean;
  isAiFoodLoggingEnabled: boolean;
  isTeamScopedPermissionsEnabled: boolean;
  isBulkDeleteMenuItemsEnabled: boolean;
  isBulkDeleteRecipesEnabled: boolean;
  menuAnalyticsDashboardEnabled: boolean;
  guestOrderingEnabled: boolean;
  isDragAndDropMenuItemsEnabled: boolean;
  useBedrock: boolean;
  isAskTeamworksEnabled: boolean;
  isGetRecipeFromUrlEnabled: boolean;
  isMealOptionGenerationHidden: boolean;
  isRecipeAndMenuItemAllergensEnabled: boolean;
  isAmsBodyCompositionEnabled: boolean;
};

/** When isTactical and query is still loading, most flags default to false. */
const LOADING_LEGACY_FLAGS = {
  isRecipeAndMenuItemRedesignEnabled: true,
  twUserManagement: false,
  appSwitcher: false,
  amsDashboard: false,
  bulkDeleteMealPlans: false,
  axleEvents: false,
  umaNotifications: false,
  bulkOrdering: false,
  isAiFoodLoggingEnabled: false,
  teamScopedPermissions: true, // default to least permissive
  showAxleUpsertTab: false,
  isUnityMessagingEnabled: false,
  isEnergyExpenditureEnabled: false,
  isMenuBuilderMultiWeekCopyingEnabled: false,
  showDeleteAthleteButton: false,
  bulkDeleteMenuItems: false,
  bulkDeleteRecipes: false,
  isRecipeAndMenuItemAllergensEnabled: false,
  isAmsBodyCompositionEnabled: false,
  useBedrock: false,
} as const;

function mapLegacyToNutritionFlags(legacy: {
  isRecipeAndMenuItemRedesignEnabled: boolean;
  twUserManagement: boolean;
  appSwitcher: boolean;
  axleEvents: boolean;
  umaNotifications: boolean;
  amsDashboard: boolean;
  bulkDeleteMealPlans: boolean;
  bulkOrdering: boolean;
  isAiFoodLoggingEnabled: boolean;
  teamScopedPermissions: boolean;
  showAxleUpsertTab: boolean;
  isUnityMessagingEnabled: boolean;
  isEnergyExpenditureEnabled: boolean;
  isMenuBuilderMultiWeekCopyingEnabled: boolean;
  showDeleteAthleteButton: boolean;
  bulkDeleteMenuItems: boolean;
  bulkDeleteRecipes: boolean;
  isRecipeAndMenuItemAllergensEnabled: boolean;
  isAmsBodyCompositionEnabled: boolean;
  useBedrock: boolean;
}): NutritionFlags {
  return {
    // Recipe and menu item redesign is enabled by default in tactical.
    isRecipeAndMenuItemRedesignEnabled: true,
    isTeamworksUserManagementEnabled: legacy.twUserManagement,
    isAppSwitcherEnabled: legacy.appSwitcher,
    isAmsDashboardEnabled: legacy.amsDashboard,
    isBulkDeleteMealPlansEnabled: legacy.bulkDeleteMealPlans,
    isAxleEventsEnabled: legacy.axleEvents,
    isUmaNotificationsEnabled: legacy.umaNotifications,
    isBulkOrderingEnabled: legacy.bulkOrdering,
    isAiFoodLoggingEnabled: legacy.isAiFoodLoggingEnabled,
    isTeamScopedPermissionsEnabled: legacy.teamScopedPermissions,
    showAxleUpsertTab: legacy.showAxleUpsertTab,
    isUnityMessagingEnabled: legacy.isUnityMessagingEnabled,
    isEnergyExpenditureEnabled: legacy.isEnergyExpenditureEnabled,
    isMenuBuilderMultiWeekCopyingEnabled: legacy.isMenuBuilderMultiWeekCopyingEnabled,
    showDeleteAthleteButton: legacy.showDeleteAthleteButton,
    isBulkDeleteMenuItemsEnabled: legacy.bulkDeleteMenuItems,
    isBulkDeleteRecipesEnabled: legacy.bulkDeleteRecipes,
    isRecipeAndMenuItemAllergensEnabled: legacy.isRecipeAndMenuItemAllergensEnabled,
    isAmsBodyCompositionEnabled: legacy.isAmsBodyCompositionEnabled,
    // Menu Order Analytics is commercial-only
    menuAnalyticsDashboardEnabled: false,
    // Guest Ordering is commercial-only
    guestOrderingEnabled: false,
    // Drag-and-drop Menu Items panel is commercial-only
    isDragAndDropMenuItemsEnabled: false,
    // Bedrock is the only AI provider in tactical, gated per-org via the legacy flag
    useBedrock: legacy.useBedrock,
    // Ask Teamworks nutrition widget is commercial-only
    isAskTeamworksEnabled: false,
    // Recipe URL transcription is available whenever Bedrock is enabled for the org
    isGetRecipeFromUrlEnabled: legacy.useBedrock,
    // Meal option generation/suggestions: not hidden in tactical (locale feature flags still apply)
    isMealOptionGenerationHidden: false,
  };
}

/**
 * Returns LaunchDarkly flags in commercial; legacy org feature flags in tactical (GovCloud).
 * When isTactical, we do not call useFlags (no LD provider); hook order is still consistent
 * because isTactical is build-time (VITE_ENV_TYPE) and does not change during app lifecycle.
 */
export const useNutritionFlags = (): NutritionFlags => {
  const { isCommercial, isTactical } = useEnvironmentType();
  const { data } = useMyFeatureFlagsQuery({ skip: isCommercial });

  if (isTactical) {
    if (!data?.myFeatureFlags) return mapLegacyToNutritionFlags(LOADING_LEGACY_FLAGS);
    const legacy = { ...LOADING_LEGACY_FLAGS, ...data.myFeatureFlags };
    return mapLegacyToNutritionFlags(legacy);
  }

  const flags = useFlags<NutritionFlags>();
  return {
    ...flags,
    // Default false when LD is unavailable or the flag key is missing
    isGetRecipeFromUrlEnabled: flags.isGetRecipeFromUrlEnabled ?? false,
    // Existing feature: default not-hidden so a missing key does not hide generation outside EU
    isMealOptionGenerationHidden: flags.isMealOptionGenerationHidden ?? false,
    isRecipeAndMenuItemAllergensEnabled: flags.isRecipeAndMenuItemAllergensEnabled ?? false,
    isAmsBodyCompositionEnabled: flags.isAmsBodyCompositionEnabled ?? false,
  };
};
